"use client";

import React from "react";
import { formatBalance, getDateFromEpochId } from "@cede/utils";
import { CountdownTimer } from "@/components/features/spinning-wheel/WheelHistory/CountdownTimer";
import { UsdtIcon } from "@/components/icons";
import { ExchangeIcon } from "@/components/icons/library/exchanges/ExchangeIcon";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import type { ClaimRewardsError } from "@/hooks";
import { useCashbackConfig } from "@/hooks/cashback/useCashbackConfig";
import { useCashbackStatus } from "@/hooks/cashback/useCashbackStatus";
import { useClaimCashbackRewards } from "@/hooks/cashback/useClaimCashbackRewards";
import { useFormatCurrency } from "@/hooks/currency";
import { CLAIM_REWARDS_ERROR_TYPE } from "@/hooks/user/useClaimRewards";
import { format } from "date-fns";
import Decimal from "decimal.js";
import { BadgePercent } from "lucide-react";
import { useSnackbar } from "notistack";

/**
 * Displays the user's cashback history with accumulated amounts and claim status
 */
export const CashbackHistory: React.FC = () => {
  const { status, isLoading: statusLoading, refetch } = useCashbackStatus();
  const { isActive: isCashbackActive } = useCashbackConfig();
  const { enqueueSnackbar } = useSnackbar();
  const { formatUsdtAsLocal, isUsd } = useFormatCurrency();

  // Claim rewards integration (only used for the claim action)
  const { claimReward, isLoading: claimLoading } = useClaimCashbackRewards({
    onClaimSuccess: () => {
      enqueueSnackbar("Cashback claimed successfully!", { variant: "success" });
      refetch();
    },
    onClaimError: (error: ClaimRewardsError) => {
      if (error.type === CLAIM_REWARDS_ERROR_TYPE.USER_REJECTED) return;
      enqueueSnackbar(error.message || "Failed to claim cashback", { variant: "error" });
    },
  });

  // Use claimable data directly from status API
  const hasClaimableRewards = status?.claimable !== null && status?.claimable !== undefined;
  const totalClaimable = status?.claimable ? new Decimal(status.claimable.amount) : new Decimal(0);

  // Use claimableSoon data directly from status API
  const hasClaimableSoon = status?.claimableSoon !== null && status?.claimableSoon !== undefined;
  const totalClaimableSoon = status?.claimableSoon ? new Decimal(status.claimableSoon.amount) : new Decimal(0);
  const cooldownEnd = status?.claimableSoon?.cooldownEnd ?? null;

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-cede-text-secondary">Loading...</div>
      </div>
    );
  }

  if (!status || !isCashbackActive) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center text-cede-text-secondary">
          <BadgePercent className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>{isCashbackActive ? "No cashback data available" : "Coming soon"}</p>
        </div>
      </div>
    );
  }

  const progressPercentage = status.unlockProgress.percentage;
  // Pending cashback (not yet assigned to an epoch) - this is what we show as "Accumulated"
  const pendingAmount = new Decimal(status.unlockProgress.current);
  const threshold = new Decimal(status.unlockProgress.threshold);
  const monthlyUsed = new Decimal(status.monthlyUsage.used);
  const monthlyClaimed = new Decimal(status.monthlyUsage.claimed);
  const monthlyCap = new Decimal(status.monthlyUsage.cap);
  const monthlyRemaining = new Decimal(status.monthlyUsage.remaining);

  // Check if there's cashback awaiting distribution (assigned to epoch but not yet on-chain)
  const awaitingDistributionAmount = status.awaitingDistribution
    ? new Decimal(status.awaitingDistribution.amount)
    : new Decimal(0);
  const hasAwaitingDistribution = status.awaitingDistribution !== null;

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Monthly Usage Stats */}
      <div className="bg-cede-bg-component rounded-large border-container p-4">
        <div className="text-xs text-cede-text-secondary mb-1">Monthly cashback limit</div>
        <div className="flex items-center gap-1 text-lg font-semibold text-cede-text-primary">
          <UsdtIcon width={18} height={18} />
          <span>
            {formatBalance(monthlyUsed, { roundForFiat: true })} / {monthlyCap.toFixed(2)}
          </span>
        </div>
        <div className="text-xs text-cede-text-tertiary">
          {formatBalance(monthlyRemaining, { roundForFiat: true })} remaining (
          {formatBalance(monthlyClaimed, { roundForFiat: true })} claimed)
        </div>
      </div>

      {/* Claimable Rewards Section */}
      {hasClaimableRewards && (
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-xs text-cede-text-secondary mb-2">Ready to Claim</div>
              <div className="flex items-center gap-1">
                <UsdtIcon width={16} height={16} />
                <span className="font-semibold text-sm">{formatBalance(totalClaimable, { roundForFiat: true })}</span>
                {!isUsd && totalClaimable.greaterThan(0) && (
                  <span className="text-sm text-cede-text-secondary">
                    ({formatUsdtAsLocal(totalClaimable.toNumber())})
                  </span>
                )}
              </div>
            </div>
            <Button onClick={() => claimReward(undefined)} disabled={claimLoading} size="medium" className="px-8 ml-4">
              {claimLoading ? "Claiming..." : "Claim All"}
            </Button>
          </div>
          {/* Epochs detail */}
          {status?.claimable?.epochs && status.claimable.epochs.length > 1 && (
            <div className="mt-3 pt-3 border-t border-cede-border space-y-2">
              {status.claimable.epochs.map((epoch) => {
                const epochAmount = new Decimal(epoch.amount);
                return (
                  <div key={epoch.epochId} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1">
                      <UsdtIcon width={14} height={14} />
                      <span className="text-cede-text-primary">
                        {formatBalance(epochAmount, { roundForFiat: true })}
                      </span>
                      {!isUsd && epochAmount.greaterThan(0) && (
                        <span className="text-xs text-cede-text-secondary">
                          ({formatUsdtAsLocal(epochAmount.toNumber())})
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-cede-text-secondary">
                      {format(getDateFromEpochId(epoch.epochId), "dd MMM yyyy")}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Claimable Soon Section */}
      {hasClaimableSoon && (
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-cede-text-secondary mb-2">Coming Soon</div>
              <div className="flex items-center gap-1">
                <UsdtIcon width={16} height={16} />
                <span className="font-semibold text-sm">
                  {formatBalance(totalClaimableSoon, { roundForFiat: true })}
                </span>
              </div>
            </div>
            {cooldownEnd && (
              <div className="text-right">
                <div className="text-xs text-cede-text-secondary mb-2">Claimable in</div>
                <div className="text-sm font-semibold">
                  <CountdownTimer endDate={new Date(cooldownEnd)} isEstimated={false} />
                </div>
              </div>
            )}
          </div>
          {/* Epochs detail */}
          {status?.claimableSoon?.epochs && status.claimableSoon.epochs.length > 1 && (
            <div className="mt-3 pt-3 border-t border-cede-border space-y-2">
              {status.claimableSoon.epochs.map((epoch) => (
                <div key={epoch.epochId} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1">
                    <UsdtIcon width={14} height={14} />
                    <span className="text-cede-text-primary">
                      {formatBalance(new Decimal(epoch.amount), { roundForFiat: true })}
                    </span>
                  </div>
                  {epoch.cooldownEnd && (
                    <div className="text-xs text-cede-text-secondary flex items-center gap-1">
                      <CountdownTimer endDate={new Date(epoch.cooldownEnd)} isEstimated={false} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Processing Section - Show when there's cashback awaiting distribution */}
      {hasAwaitingDistribution && (
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-cede-text-secondary mb-1">Processing</div>
              <div className="flex items-center gap-1">
                <UsdtIcon width={16} height={16} />
                <span className="text-sm font-semibold text-cede-primary">
                  {formatBalance(awaitingDistributionAmount, { roundForFiat: true })}
                </span>
              </div>
            </div>
            <div className="text-xs text-cede-text-secondary text-right">Claimable soon</div>
          </div>
          <div className="text-xs text-cede-text-secondary mt-3">
            Your cashback is being prepared for distribution. It will be claimable soon.
          </div>
        </div>
      )}

      {/* Pending Rewards Section - Show when there are pending rewards accumulating towards threshold */}
      {pendingAmount.greaterThan(0) && (
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="flex flex-col gap-3">
            <div>
              <div className="text-xs text-cede-text-secondary mb-1">Pending Rewards</div>
              <div className="flex items-center gap-1">
                <UsdtIcon width={16} height={16} />
                <span className="text-sm font-semibold text-cede-primary">
                  {formatBalance(pendingAmount, { roundForFiat: true })} / {threshold.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-cede-text-secondary">{progressPercentage.toFixed(0)}% of threshold</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>

            <div className="text-xs text-cede-text-secondary">
              {progressPercentage >= 100
                ? "Threshold reached! Your cashback will be distributed soon."
                : `Keep trading! Cashback becomes claimable when you reach ${threshold.toFixed(2)}.`}
            </div>

            {/* Pending Transactions Dropdown */}
            {status?.unlockProgress?.pendingTransactions && status.unlockProgress.pendingTransactions.length > 0 && (
              <Accordion type="single" collapsible className="mt-2">
                <AccordionItem value="pending-transactions" className="border-0">
                  <AccordionTrigger className="py-2 text-xs hover:no-underline">
                    <span>
                      {status.unlockProgress.pendingTransactions.length >= 20
                        ? "View last 20 transactions"
                        : `View ${status.unlockProgress.pendingTransactions.length} transaction(s)`}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-0">
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {status.unlockProgress.pendingTransactions.map((tx) => (
                        <div
                          key={tx.id}
                          className="flex items-center justify-between py-2 px-3 bg-cede-bg-secondary rounded-medium"
                        >
                          <div className="flex items-center gap-3">
                            <ExchangeIcon exchange={tx.exchangeId} size="sm" />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-cede-text-primary">{tx.tradingPair}</span>
                              <span className="text-xs text-cede-text-tertiary">
                                {format(new Date(tx.createdAt), "dd MMM, HH:mm")}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-sm font-semibold text-cede-success">
                            <UsdtIcon width={12} height={12} />
                            <span>+{new Decimal(tx.cashbackAmount).toFixed(5)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {pendingAmount.equals(0) && !hasClaimableRewards && !hasClaimableSoon && !hasAwaitingDistribution && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center text-cede-text-secondary">
            <BadgePercent className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No cashback yet. Start trading to earn cashback!</p>
          </div>
        </div>
      )}
    </div>
  );
};
